import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  FileText,
  CheckCircle2,
  Clock,
  Eye,
  Save,
  X,
  RotateCcw,
  FileSpreadsheet,
  Plus,
  Trash2,
  Settings2,
  PlusCircle,
  Search,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Play,
  Filter,
  Columns,
  FolderOpen,
  History,
  MoreVertical,
  Download,
} from 'lucide-react';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { useAdditionsPermissions } from '@/hooks/use-permissions';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { FeatureSettingsGuard } from '@/components/features/FeatureSettingsGuard';
import { ProductTour } from '@/components/ui/product-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useTour } from '@/contexts/TourContext';
import { useAdditionsTour } from '@/hooks/use-additions-tour';
import type { Asset } from '@/types/asset';
import { dataService, getDataSourceMode } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { formatApiErrorMessage } from '@/lib/format-utils';
import { exportToCSV, exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { mockAdditionBatches, type AdditionBatch, type AdditionItem } from '@/lib/mock-additions-data';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { AdditionsFiltersTab } from '@/components/RightPanel/AdditionsFiltersTab';
import { AdditionsColumnsTab } from '@/components/RightPanel/AdditionsColumnsTab';
import { DEFAULT_ADDITIONS_COLUMNS } from '@/lib/additions-columns-config';
import { KpiCard } from '@/components/kpi-card';
import { TrendingUp, Coins, Package } from 'lucide-react';
import { AdditionEntryScreen } from '@/components/transactions/addition-entry-screen';

const getAdditionExportColumns = (): ExportColumn<AdditionItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof AdditionItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof AdditionItem },
  { label: 'Category', key: 'category' as keyof AdditionItem },
  { label: 'Location', key: 'location' as keyof AdditionItem },
  { label: 'Value', key: 'value' as keyof AdditionItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Notes', key: 'notes' as keyof AdditionItem },
];

const getDataSource = getDataSourceMode;

type SortDirection = 'asc' | 'desc' | null;

interface SortIconProps {
  readonly columnKey: string;
  readonly sortKey: string | null;
  readonly sortDirection: SortDirection;
}

function SortIcon({ columnKey, sortKey, sortDirection }: SortIconProps): React.ReactElement | null {
  if (sortKey !== columnKey) return null;
  return sortDirection === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Helper function to get sort value for a batch - extracted to reduce cognitive complexity (S3776)
function getAdditionBatchSortValue(batch: AdditionBatch, sortKey: string): string | number {
  switch (sortKey) {
    case 'reference':
      return batch.batchReference;
    case 'date':
      return new Date(batch.dateOfRun).getTime();
    case 'periodType':
      return batch.periodType;
    case 'totalValue':
      return batch.totalValue;
    case 'postedBy':
      return batch.postedBy ?? '';
    case 'postedDate':
      return batch.postedDate ? new Date(batch.postedDate).getTime() : 0;
    case 'status':
      return batch.status;
    default:
      return 0;
  }
}

// Helper function to compare sort values - extracted to reduce cognitive complexity (S3776)
function compareAdditionSortValues(aValue: string | number, bValue: string | number, direction: 'asc' | 'desc'): number {
  if (aValue === null || aValue === undefined) return 1;
  if (bValue === null || bValue === undefined) return -1;

  if (direction === 'asc') {
    if (aValue > bValue) return 1;
    if (aValue < bValue) return -1;
    return 0;
  }
  // direction === 'desc'
  if (aValue < bValue) return 1;
  if (aValue > bValue) return -1;
  return 0;
}

// Helper function to generate page numbers for pagination - extracted to reduce nesting depth (S2004)
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pageNumbers: (number | string)[] = [];
  const maxVisiblePages = 7;
  
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1);
    
    if (currentPage > 3) {
      pageNumbers.push('...');
    }
    
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    pageNumbers.push(totalPages);
  }
  
  return pageNumbers;
}

// Helper function to render a single page button - extracted to reduce nesting depth (S2004)
function renderPageButton(
  page: number | string,
  pageIndex: number,
  pageNumbers: (number | string)[],
  currentPage: number,
  setCurrentPage: (page: number) => void
): React.ReactElement {
  if (page === '...') {
    return (
      <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
        ...
      </span>
    );
  }
  
  return (
    <Button
      key={`page-${page}`}
      variant={currentPage === page ? 'default' : 'outline'}
      size="sm"
      onClick={() => setCurrentPage(page as number)}
      className="min-w-[36px]"
    >
      {page}
    </Button>
  );
}

function AdditionsPageContent(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { canCreate } = useAdditionsPermissions();
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useAdditionsTour();
  const { setOnStartTour } = useTour();
  
  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => {
      setOnStartTour(null);
    };
  }, [setOnStartTour, startTour]);
  
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<AdditionBatch | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [modifiedHighlight, setModifiedHighlight] = useState(false);
    const [completedBatches, setCompletedBatches] = useState<AdditionBatch[]>(mockAdditionBatches);
    const [isLoadingBatches, setIsLoadingBatches] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalBatch, setOriginalBatch] = useState<AdditionBatch | null>(null);
    const [showComingSoon, setShowComingSoon] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
    key: null,
    direction: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        // Fetch all batches (both completed and in-progress)
        const batches = await dataService.getAdditionBatches();
        if (batches && batches.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun || b.createdDate).getTime() - new Date(a.dateOfRun || a.createdDate).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          // Only show mock data in demo mode
          if (getDataSource() === 'demo') {
            setCompletedBatches(mockAdditionBatches);
          } else {
            setCompletedBatches([]);
          }
        }
      } catch (error) {
        console.error('Error fetching addition batches:', error);
        // Only show mock data in demo mode
        if (getDataSource() === 'demo') {
          setCompletedBatches(mockAdditionBatches);
        } else {
          setCompletedBatches([]);
        }
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (!currentBatch) {
      fetchBatches();
    }
  }, [currentBatch, refreshTrigger]);

  // Handle selected assets passed from assets page and URL parameters
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[]; refresh?: boolean; viewBatch?: boolean } | null;
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('batch');
    
    // Handle refresh from breadcrumb click (when clicking current page without batch param)
    if (state?.refresh && !batchId) {
      // Clear the current batch and reset page state
      setCurrentBatch(null);
      setSearchQuery('');
      setSortConfig({ key: null, direction: null });
      setRefreshTrigger(prev => prev + 1); // Trigger data refetch
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Handle clearing batch when navigating back to main page (no batch param)
    if (!batchId && currentBatch) {
      setCurrentBatch(null);
      setRefreshTrigger(prev => prev + 1);
      return;
    }
    
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.search]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleStartRun = () => {
    setShowAssetSelection(true);
  };

  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };

  const handleViewLastBatchSummary = async () => {
    if (!completedBatches || completedBatches.length === 0) {
      toast.error('No batch history available', { 
        description: 'There are no completed batches to view.' 
      });
      return;
    }

    try {
      const lastBatch = completedBatches[0]; // Already sorted by date descending
      
      // Navigate to the batch view URL
      navigate(`/transactions/additions?batch=${lastBatch.id}`, {
        state: { viewBatch: true }
      });
      
      // Load full batch data including items
      if (lastBatch.id && isServerGeneratedId(lastBatch.id)) {
        const fullBatch = await dataService.getAdditionBatch(lastBatch.id);
        // Ensure date is in YYYY-MM-DD format for date input
        const formattedBatch = {
          ...fullBatch,
          dateOfRun: fullBatch.dateOfRun.split('T')[0],
        };
        setCurrentBatch(formattedBatch);
        setOriginalBatch(JSON.parse(JSON.stringify(formattedBatch)));
        setHasUnsavedChanges(false);
        toast.success('Batch loaded', { 
          description: `Viewing batch from ${formattedBatch.dateOfRun}` 
        });
      } else {
        setCurrentBatch(lastBatch);
        setOriginalBatch(JSON.parse(JSON.stringify(lastBatch)));
        setHasUnsavedChanges(false);
        toast.success('Batch loaded', { 
          description: `Viewing batch from ${lastBatch.dateOfRun}` 
        });
      }
    } catch (error) {
      console.error('Error loading last batch:', error);
      // Fallback to using the batch data we have
      const lastBatch = completedBatches[0];
      setCurrentBatch(lastBatch);
      setOriginalBatch(JSON.parse(JSON.stringify(lastBatch)));
      setHasUnsavedChanges(false);
      toast.warning('Batch loaded from cache', { 
        description: 'Could not fetch latest data from server, showing cached version.' 
      });
    }
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    
    const updatedItems = currentBatch.items.filter(item => !selectedItemIds.has(item.id));
    const totalValue = updatedItems.reduce((sum, item) => sum + item.value, 0);
    
    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalValue,
    });
    
    setSelectedItemIds(new Set());
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create addition items from selected assets
    const newItems: AdditionItem[] = selectedAssets.map((asset, index) => ({
      id: `add-item-${Date.now()}-${index}`,
      assetId: asset.id,
      assetCode: asset.code,
      assetDescription: asset.name,
      category: asset.category ?? '',
      location: asset.location ?? '',
      currentNBV: asset.netBookValue ?? 0,
      value: 0,
      notes: '',
    }));

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalValue = allItems.reduce((sum, item) => sum + item.value, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalValue,
      });
    } else {
      // Create new batch
      const totalValue = newItems.reduce((sum, item) => sum + item.value, 0);

      const now = new Date().toISOString();
      const batch: AdditionBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        periodType: 'current',
        status: 'In Progress',
        notes: '',
        items: newItems,
        preparedBy: 'Current User',
        preparedDate: now,
        totalValue,
        currency: 'GBP',
        createdBy: 'Current User',
        createdDate: now,
        modifiedBy: 'Current User',
        modifiedDate: now,
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  const handleUpdateItem = (itemId: string, value: number) => {
    if (!currentBatch) return;

    const updatedItems = currentBatch.items.map((item) =>
      item.id === itemId ? { ...item, value } : item
    );

    const totalValue = updatedItems.reduce((sum, item) => sum + item.value, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalValue,
    });
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!currentBatch) return;
    
    try {
      const now = new Date().toISOString();
      const batchToSave = {
        ...currentBatch,
        modifiedBy: 'Current User',
        modifiedDate: now,
        status: currentBatch.status || 'In Progress',
      };
      
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        const updatedBatch = await dataService.updateAdditionBatch(currentBatch.id, batchToSave);
        // Ensure date is in YYYY-MM-DD format for date input
        const formattedBatch = {
          ...updatedBatch,
          dateOfRun: updatedBatch.dateOfRun.split('T')[0],
        };
        setCurrentBatch(formattedBatch);
      } else {
        const savedBatch = await dataService.createAdditionBatch(batchToSave);
        // Ensure date is in YYYY-MM-DD format for date input
        const formattedBatch = {
          ...savedBatch,
          dateOfRun: savedBatch.dateOfRun.split('T')[0],
        };
        setCurrentBatch(formattedBatch);
      }
      
      setHasUnsavedChanges(false);
      setOriginalBatch(JSON.parse(JSON.stringify(batchToSave)));
      setModifiedHighlight(true);
      setTimeout(() => setModifiedHighlight(false), 2000);
      
      toast.success('Batch saved', { description: 'Addition batch saved successfully' });
    } catch (error) {
      console.error('Failed to save addition batch:', error);
      toast.error('Failed to save batch', { description: 'Please ensure you are in API mode and try again.' });
    }
  };

  const handleProcess = () => {
    if (!currentBatch) return;
    
    // Validate mandatory fields
    if (!currentBatch.batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before processing.',
      });
      return;
    }
    
    if (!currentBatch.dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }
    
    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    if (!currentBatch) return;
    
    const reference = currentBatch.batchReference;
    
    try {
      let batchId = currentBatch.id;
      
      // Ensure batch is saved first to get a server-generated ID
      if (!isServerGeneratedId(currentBatch.id)) {
        const savedBatch = await dataService.createAdditionBatch(currentBatch);
        batchId = savedBatch.id;
      } else {
        // Update the batch to ensure latest changes are saved
        await dataService.updateAdditionBatch(currentBatch.id, currentBatch);
      }
      
      // Call the backend PostBatch endpoint which creates transactions and posts to Financials
      const result = await dataService.postAdditionBatch(batchId, 'Current User');
      
      // Check for posting failures
      const postingResult = result?.postingResult;
      const hasCreationFailures = postingResult?.transactionCreationFailures?.length > 0;
      const hasPostingFailures = postingResult?.financialsPostingFailures?.length > 0;
      
      if (hasCreationFailures || hasPostingFailures) {
        // Build failure message
        const failureMessages: string[] = [];
        const errorDetails: string[] = [];
        
        if (hasCreationFailures) {
          const count = postingResult.transactionCreationFailures.length;
          failureMessages.push(`${count} transaction(s) failed to create`);
          postingResult.transactionCreationFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            console.error(`Transaction creation failed for asset ${failure.assetCode}: ${failure.errorMessage}`);
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        if (hasPostingFailures) {
          const count = postingResult.financialsPostingFailures.length;
          failureMessages.push(`${count} transaction(s) failed to post to Financials`);
          postingResult.financialsPostingFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            console.error(`Financials posting failed for asset ${failure.assetCode}: ${failure.errorMessage}`);
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        // Build description with error details (show first 3 errors, then indicate if more)
        const maxErrorsToShow = 3;
        const displayedErrors = errorDetails.slice(0, maxErrorsToShow);
        const remainingCount = errorDetails.length - maxErrorsToShow;
        let description = `Reference: ${reference}. ${failureMessages.join('. ')}.\n${displayedErrors.join('\n')}`;
        if (remainingCount > 0) {
          description += `\n...and ${remainingCount} more error(s)`;
        }
        
        // Show warning toast with failure details
        toast.warning('Addition Completed with Warnings', {
          description,
          duration: 10000,
        });
      }else {
        // Show success toast
        toast.success('Addition Created Successfully', {
          description: `Reference: ${reference}`,
        });
      }
      
      // Clear batch and navigate back to processing screen
      setCurrentBatch(null);
      setSelectedItemIds(new Set());
      setShowProcessConfirm(false);
      
      // Navigate back to processing screen
      navigate('/transactions/additions');
    } catch (error) {
      console.error('Failed to process addition batch:', error);
      toast.error('Failed to process addition batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancelBatch = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      // If no unsaved changes, just go back
      setCurrentBatch(null);
    }
  };

  const confirmCancelBatch = () => {
    setCurrentBatch(null);
    setHasUnsavedChanges(false);
  };

  // Sorting handler
  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current.key === key) {
        // Cycle through: asc -> desc -> null
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (current.direction === 'desc') {
          return { key: null, direction: null };
        }
      }
      // Start with ascending for new column
      return { key, direction: 'asc' };
    });
    setCurrentPage(1);
  };

  // Filter and sort batches - using extracted helper functions to reduce cognitive complexity (S3776)
  const filteredBatches = completedBatches
    .filter((batch) => {
      if (!searchQuery.trim()) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        batch.batchReference.toLowerCase().includes(query) ||
        batch.notes.toLowerCase().includes(query) ||
        batch.preparedBy.toLowerCase().includes(query) ||
        batch.postedBy?.toLowerCase().includes(query) ||
        batch.status.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      if (!sortConfig.key || !sortConfig.direction) return 0;

      const aValue = getAdditionBatchSortValue(a, sortConfig.key);
      const bValue = getAdditionBatchSortValue(b, sortConfig.key);
      return compareAdditionSortValues(aValue, bValue, sortConfig.direction);
    });

  // Right Panel Configuration
  const { visibleColumns, columnOrder, setVisibleColumns, setColumnOrder, activeFilterCount } = useRightPanel();
  
  // Initialize columns on mount
  useEffect(() => {
    if (visibleColumns.length === 0) {
      const defaultVisible = DEFAULT_ADDITIONS_COLUMNS
        .filter(col => col.visible)
        .map(col => col.id);
      setVisibleColumns(defaultVisible);
    }
    if (columnOrder.length === 0) {
      setColumnOrder(DEFAULT_ADDITIONS_COLUMNS.map(col => col.id));
    }
  }, [visibleColumns.length, columnOrder.length, setVisibleColumns, setColumnOrder]);

  // Right Panel Tabs
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <AdditionsFiltersTab />,
      showBadge: true,
      badgeCount: activeFilterCount,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <AdditionsColumnsTab />,
    },
  ];

  // Landing Dashboard View
  if (!currentBatch) {
    const content = (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6 flex-shrink-0" data-tour="additions-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <PlusCircle className="h-6 w-6 text-primary" />
                Additions
              </h1>
              <p className="text-sm text-muted-foreground mt-1">View, create, or edit transactions that increase asset net book value.</p>
            </div>
                        <div className="flex items-center gap-2">
                          <EvoFeatureFeedbackCollector feature="Addition Transactions" />
                          <Button variant="outline" onClick={handleViewLastBatchSummary} data-tour="view-summary-button">
                            <Eye className="mr-2 h-4 w-4" />
                            View Last Batch Summary
                          </Button>
                          {canCreate && (
                            <Button onClick={handleStartRun} data-tour="start-batch-button">
                              <Play className="mr-2 h-4 w-4" />
                              Start New Addition Batch
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                {/* Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* KPI Cards */}
          <div className="p-6 pb-0 flex-shrink-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
              <KpiCard
                icon={TrendingUp}
                title="Total Additions (YTD)"
                value={formatCurrency(completedBatches.reduce((sum, batch) => sum + batch.totalValue, 0))}
                description="Year-to-date additions value"
              />
              <KpiCard
                icon={Coins}
                title="Average Addition Value"
                value={formatCurrency(
                  completedBatches.length > 0
                    ? completedBatches.reduce((sum, batch) => sum + batch.totalValue, 0) / completedBatches.length
                    : 0
                )}
                description="Average value per batch"
              />
              <KpiCard
                icon={Package}
                title="Number of Batched Additions"
                value={completedBatches.length.toLocaleString()}
                description="Total batches completed this year"
              />
            </div>
          </div>

          {/* Transaction History Header */}
          <div className="bg-white dark:bg-black border-t border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Transaction History</h2>
                <p className="text-sm text-muted-foreground mt-1">All addition batches (in progress and completed)</p>
              </div>
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white dark:bg-slate-900"
                  data-tour="search-transactions"
                />
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="flex-1 flex flex-col min-h-0">
          
          {(() => {
            // Pagination calculations
            const totalPages = Math.ceil(filteredBatches.length / pageSize);
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedBatches = filteredBatches.slice(startIndex, endIndex);

            if (isLoadingBatches) {
              return (
                <div className="flex h-full items-center justify-center py-12">
                  <div className="text-center">
                    <p className="text-lg font-medium">Loading batches...</p>
                  </div>
                </div>
              );
            }

            if (filteredBatches.length === 0) {
              return (
            <div className="flex flex-col items-center justify-center space-y-4 py-12">
              <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                <FileText className="h-12 w-12 text-slate-400 dark:text-slate-500" />
              </div>
              <div className="space-y-2 text-center">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No transactions found</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  {searchQuery ? "Try adjusting your search to find what you're looking for" : 'No batches found'}
                </p>
              </div>
            </div>
              );
            }

            return (
            <div className="flex-1 flex flex-col min-h-0" data-tour="transaction-history">
              <div className="flex-1 overflow-auto">
              <Table className="w-full">
                <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                  <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('reference')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Reference
                        <SortIcon columnKey="reference" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>

                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('date')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Date
                        <SortIcon columnKey="date" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('period')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Period
                        <SortIcon columnKey="period" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('year')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Year
                        <SortIcon columnKey="year" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('periodType')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Period Type
                        <SortIcon columnKey="periodType" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold text-right">
                      <button
                        onClick={() => handleSort('totalValue')}
                        className="flex items-center ml-auto hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Total Value
                        <SortIcon columnKey="totalValue" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('postedBy')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Posted By
                        <SortIcon columnKey="postedBy" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('postedDate')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Posted Date
                        <SortIcon columnKey="postedDate" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      >
                        Status
                        <SortIcon columnKey="status" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                      </button>
                    </TableHead>
                    <TableHead className="h-12 font-semibold text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBatches.map((batch, index) => (
                    <TableRow 
                      key={batch.id} 
                      className={`
                        ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                        hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                      `}
                    >
                      <TableCell className="font-semibold">{batch.batchReference}</TableCell>
                      <TableCell>{new Date(batch.dateOfRun).toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>{batch.period ?? '-'}</TableCell>
                      <TableCell>{batch.year ?? '-'}</TableCell>
                      <TableCell>{batch.periodType === 'current' ? 'Current Period' : 'Service Period'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(batch.totalValue)}
                      </TableCell>
                      <TableCell>{batch.postedBy ?? '-'}</TableCell>
                      <TableCell>
                        {batch.postedDate ? new Date(batch.postedDate).toLocaleString('en-GB') : '-'}
                      </TableCell>
                      <TableCell data-tour="batch-status">
                        <Badge 
                          variant="secondary"
                          className={
                            batch.status === 'Completed'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          }
                        >
                          {batch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {batch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
                          {batch.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              // Load full batch data including items
                              try {
                                if (batch.id && isServerGeneratedId(batch.id)) {
                                  const fullBatch = await dataService.getAdditionBatch(batch.id);
                                  // Ensure date is in YYYY-MM-DD format for date input
                                  const formattedBatch = {
                                    ...fullBatch,
                                    dateOfRun: fullBatch.dateOfRun.split('T')[0],
                                  };
                                  setCurrentBatch(formattedBatch);
                                  setOriginalBatch(JSON.parse(JSON.stringify(formattedBatch)));
                                  setHasUnsavedChanges(false);
                                } else {
                                  setCurrentBatch(batch);
                                  setOriginalBatch(JSON.parse(JSON.stringify(batch)));
                                  setHasUnsavedChanges(false);
                                }
                              } catch (error) {
                                console.error('Error loading batch:', error);
                                setCurrentBatch(batch);
                                setOriginalBatch(JSON.parse(JSON.stringify(batch)));
                                setHasUnsavedChanges(false);
                              }
                            }}
                          >
                            <FolderOpen className="h-4 w-4 mr-2" />
                            Open
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigate(`/enquiry?reference=${encodeURIComponent(batch.batchReference)}&transactionType=Addition`);
                            }}
                            title="View this transaction in Transaction History"
                          >
                            <History className="h-4 w-4 mr-2" />
                            View Transaction History
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={async () => {
                                let items: AdditionItem[] | undefined = batch.items;
                                if ((!items || items.length === 0) && batch.id && isServerGeneratedId(batch.id)) {
                                  try {
                                    const fullBatch = await dataService.getAdditionBatch(batch.id);
                                    items = fullBatch.items;
                                  } catch {
                                    items = items ?? [];
                                  }
                                }
                                if (!items || items.length === 0) { toast.error('No data to export'); return; }
                                exportToCSV({ data: items, columns: getAdditionExportColumns(), filename: generateFilename(`addition-batch-${batch.batchReference || 'draft'}`, 'csv') });
                              }}>
                                <Download className="mr-2 h-4 w-4" />
                                Export CSV
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={async () => {
                                let items: AdditionItem[] | undefined = batch.items;
                                if ((!items || items.length === 0) && batch.id && isServerGeneratedId(batch.id)) {
                                  try {
                                    const fullBatch = await dataService.getAdditionBatch(batch.id);
                                    items = fullBatch.items;
                                  } catch {
                                    items = items ?? [];
                                  }
                                }
                                if (!items || items.length === 0) { toast.error('No data to export'); return; }
                                exportToExcel({ data: items, columns: getAdditionExportColumns(), filename: generateFilename(`addition-batch-${batch.batchReference || 'draft'}`, 'xlsx'), sheetName: 'Addition Details' });
                              }}>
                                <Download className="mr-2 h-4 w-4" />
                                Export Excel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              
              {/* Pagination Footer - Always visible */}
              <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-black">
                <div className="text-sm text-muted-foreground flex-shrink-0">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredBatches.length)} of {filteredBatches.length} transactions
                  {filteredBatches.length !== completedBatches.length && ` (filtered from ${completedBatches.length} total)`}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    {/* Page Number Buttons */}
                    <div className="flex items-center gap-1">
                      {generatePageNumbers(currentPage, totalPages).map((page, pageIndex, pageNumbers) =>
                        renderPageButton(page, pageIndex, pageNumbers, currentPage, setCurrentPage)
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        </div>
      </div>

        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          title="Select Assets for Addition"
          description="Choose one or more assets to add to this addition batch."
          excludeAssetIds={[]}
        />

        <ComingSoonModal
          open={showComingSoon}
          onOpenChange={setShowComingSoon}
          title="Batch Summary Coming Soon"
          description="The ability to view the last batch summary will be available in an upcoming release."
        />

        <ProductTour
          steps={tourSteps}
          isOpen={isTourOpen}
          onClose={closeTour}
          onComplete={completeTour}
        />
      </div>
    );

    return <PageWithRightPanel>{content}<RightPanelBase tabs={rightPanelTabs} /></PageWithRightPanel>;
  }

  // Batch Processing View - Use new entry screen component
  return (
    <AdditionEntryScreen
      batch={currentBatch}
      onClose={() => setCurrentBatch(null)}
      onSave={async (batch) => {
        try {
          const now = new Date().toISOString();
          const batchToSave = {
            ...batch,
            modifiedBy: 'Current User',
            modifiedDate: now,
            status: batch.status || 'In Progress',
          };
          
          if (batch.id && isServerGeneratedId(batch.id)) {
            const updatedBatch = await dataService.updateAdditionBatch(batch.id, batchToSave);
            // Ensure date is in YYYY-MM-DD format for date input
            const formattedBatch = {
              ...updatedBatch,
              dateOfRun: updatedBatch.dateOfRun.split('T')[0],
            };
            setCurrentBatch(formattedBatch);
          } else {
            const savedBatch = await dataService.createAdditionBatch(batchToSave);
            // Ensure date is in YYYY-MM-DD format for date input
            const formattedBatch = {
              ...savedBatch,
              dateOfRun: savedBatch.dateOfRun.split('T')[0],
            };
            setCurrentBatch(formattedBatch);
          }
          
          setHasUnsavedChanges(false);
          setOriginalBatch(JSON.parse(JSON.stringify(batchToSave)));
          setModifiedHighlight(true);
          setTimeout(() => setModifiedHighlight(false), 2000);
        } catch (error) {
          console.error('Failed to save addition batch:', error);
          throw error;
        }
      }}
    />
  );
}

export default function AdditionsPage(): React.ReactElement {
  return (
    <FeatureSettingsGuard>
      <AdditionsPageContent />
    </FeatureSettingsGuard>
  );
}
